import {
  Button,
  Col,
  Divider,
  PaginationProps,
  Row,
  Space,
  Table,
  TableColumnType,
  Tooltip,
  Typography,
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  bulkCompleteService,
  completeService,
  putOrderRepairOnSiteByOrderNo,
} from "../../../apis/Order";
import { fetchReservations } from "../../../apis/Reservation/apis";
import { ReservationListSearchQuery } from "../../../apis/Reservation/apis/types";
import useModals from "../../../hooks/useModals";
import useReservation from "../../../hooks/useReservation";
import useShop from "../../../hooks/useShop";
import useUser from "../../../hooks/useUsers";
import { useWindowResize } from "../../../hooks/useWIndowResize";
import { RepairShop } from "../../../model/RepairShop";
import { Reservation } from "../../../model/Reservation";
import { Delivery } from "../../../model/ReservationDetail";
import { HttpError } from "../../../network/types";
import { PartnerBosRepairStatuses } from "../../../shared/enums";
import { alertError, alertSuccess } from "../../../shared/utils";
import { getDecodeToken } from "../../../shared/utils/Token";
import color from "../../../theme/color";
import { InterfaceNotice } from "../Icon";
import { IconForm } from "../Icon/index.styled";
import { TableForm } from "../Table";
import { TableFooterCount } from "../Table/FooterCount";
import { TablePagiCountForm } from "../Table/PageCount";
import { CustomCard } from "./Detail/Card.styled";
import { ReservationFilterForm } from "./Filter";
import { JobTaskForm } from "./JobTask";
import { ReservationOnsiteDateTimeChangeModal } from "./Modal/Onsite/DateTime";
import { ReservationNoticeInfo } from "./NoticeInfo";
import { RepairStatusTag } from "./Tags/RepairStatus";
import { ConfirmEm } from "./index.styled";
import { getModelAllYr } from "./utils/modelYear";

export const ReservationListForm = () => {
  const { width, height } = useWindowResize();
  const { getAdminInfo, signOut, getUserID } = useUser();
  const { shop } = useShop();
  const { openModal, closeModal, openConfirmModal, openAlertModal } =
    useModals();
  const { reservation, setReservation } = useReservation();

  const containerRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [expended, setExpended] = useState(true);

  const [filterItems, setFilterItems] = useState<ReservationListSearchQuery>({
    repairShopId: +shop,
    page: 1,
    pageSize: 20,
  });

  /**
   * 예약현황 조회
   */
  const { data, refetch, isRefetching, isLoading } = useQuery(
    ["reservations", filterItems],
    () =>
      fetchReservations({
        ...filterItems,
      }),
    {
      keepPreviousData: true,
      refetchInterval: 1000 * 60 * 3,
      onSuccess: () => {
        setReservation({});
      },
      onError: (error: any) => {
        alertError(error.message);
      },
    }
  );

  /**
   * 작업완료 처리 API
   */
  const { mutateAsync: completeServiceMutateAsync } = useMutation(
    completeService,
    {
      onSuccess: () => {
        alertSuccess("작업완료되었습니다.");
        closeModal();
        refetch();
      },
      onError: (err: HttpError) => {
        closeModal();
        if (err.status === 401 || err.status === 403) {
          signOut();
        }
        if (
          err.notTranslate ===
          "CANNOT_COMPLETED_SERVICE_UNTIL_ALL_COMPLETED_GOODS_ISSUE"
        )
          openAlertModal({
            contents: (
              <>
                주문상품이 모두 <ConfirmEm>&lsquo;출고완료&rsquo;</ConfirmEm>
                상태가 되어야 <ConfirmEm>&lsquo;작업 완료&lsquo;</ConfirmEm>가
                가능합니다
              </>
            ),
            title: "작업완료 불가",
            okText: "닫기",
          });
        else alertError(err.message);
      },
    }
  );

  /**
   * 일괄 작업완료 API
   */
  const { mutate: bulkCompleteServiceMutate } = useMutation(
    bulkCompleteService,
    {
      onSuccess: (data) => {
        alertSuccess("일괄작업완료 요청에 성공했습니다.");

        openModal(
          <JobTaskForm
            taskId={data.taskId}
            onClose={() => {
              closeModal();
              refetch();
              setSelectedRowKeys([]);
            }}
          />
        );
      },
      onError: (err: HttpError) => {
        closeModal();
        if (err.status === 401 || err.status === 403) {
          signOut();
        }
        if (
          err.notTranslate ===
          "CANNOT_COMPLETED_SERVICE_UNTIL_ALL_COMPLETED_GOODS_ISSUE"
        )
          openAlertModal({
            contents: (
              <>
                주문상품이 모두 <ConfirmEm>&lsquo;출고완료&rsquo;</ConfirmEm>
                상태가 되어야 <ConfirmEm>&lsquo;작업 완료&lsquo;</ConfirmEm>가
                가능합니다
              </>
            ),
            title: "작업완료 불가",
            okText: "닫기",
          });
        else alertError(err.message);
      },
    }
  );

  /**
   * 변경로직 API
   */

  const { mutate: changeOnsiteMutate, isLoading: isOnsiteLoading } =
    useMutation(putOrderRepairOnSiteByOrderNo, {
      onSuccess: () => {
        alertSuccess("예약일시가 변경되었습니다.");
        closeModal();
        refetch();
      },
      onError: (err: HttpError) => {
        alertError(err.message);
      },
    });

  const master = useMemo(() => {
    const info = getDecodeToken();

    return info?.isMaster && +shop === getUserID();
  }, [shop]);

  /**
   * @history (240514) 엔진오일 구조변경하면서 일부 컬럼 수정
   * @history 마스터계정 아닐 때 필터를 위해 임의 hidden 항목 추가
   * @history (240530) 출장은 자동으로 출장접수완료 이기때문에 버튼 제어 상태 추가
   */
  const columns: TableColumnType<Reservation>[] = useMemo(
    () =>
      [
        {
          dataIndex: "repairNo",
          key: "repairNo",
          width: 200,
          title: "주문번호",
          align: "center",
          fixed: "left",
        },
        {
          ...((master && {
            dataIndex: "repairShop",
            width: 120,
            title: "매장코드",
            align: "center",
            render: (value) => {
              return value ? value.repairShopCode || "-" : "-";
            },
          }) || { hidden: true }),
        },
        {
          ...((master && {
            dataIndex: "repairShop",
            width: 200,
            align: "center",
            title: "매장명",
            render: (value: RepairShop) => {
              return value ? value.displayName || "-" : "-";
            },
          }) || { hidden: true }),
        },
        {
          dataIndex: "reservedAt",
          key: "reservedAt",
          width: getAdminInfo()?.repairShopType === "출장교체" ? 240 : 200,
          title: "예약일시",
          align: "center",
          render: (value, record) => {
            return (
              <Row gutter={8} align="middle" justify="center">
                <Col>
                  {value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"}
                </Col>
                {!master && getAdminInfo()?.repairShopType === "출장교체" && (
                  <Col>
                    <Button
                      ghost
                      size="small"
                      type="default"
                      shape="round"
                      style={{
                        fontSize: 13,
                        height: 18,
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                      disabled={
                        record.onSite &&
                        !["PENDING", "COMPLETED_PAYMENT", "APPROVED"].includes(
                          record.onSite.onSiteStatus
                        )
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(
                          <ReservationOnsiteDateTimeChangeModal
                            defaultValue={value}
                            onClose={(value) => {
                              if (value) {
                                changeOnsiteMutate({
                                  orderNo: record.repairNo,
                                  onSitedAt: value?.toISOString(),
                                });
                              }
                            }}
                          />
                        );
                      }}
                    >
                      변경
                    </Button>
                  </Col>
                )}
              </Row>
            );
          },
        },
        {
          dataIndex: "order",
          key: "order",
          title: "고객명",
          width: 112,
          align: "center",
          render: (value) => {
            return value?.consumerName || "-";
          },
        },
        {
          dataIndex: "customerVehicleNo",
          key: "customerVehicleNo",
          title: "차량번호",
          width: 112,
          align: "center",
        },
        {
          dataIndex: "vehicle",
          key: "vehicleModel",
          title: "차종",
          width: 200,
          align: "center",
          render: (value, record) =>
            value?.vehicleDetailGroupName ||
            value?.displayVehicleModel ||
            record?.vehicleModel ||
            "-",
        },
        {
          dataIndex: "vehicle",
          key: "modelYear",
          title: "모델년도",
          width: 120,
          align: "center",
          render: (value) => getModelAllYr(value) || "-",
        },
        {
          dataIndex: "valet",
          key: "valet",
          title: "방문방법",
          width: 100,
          align: "center",
          render: (value) => (value?.valetNo ? "발렛장착" : "고객방문"),
        },
        {
          dataIndex: "goodsIssue",
          key: "goodsIssue",
          title: "운송장번호",
          width: 120,
          align: "center",
          render: (value) => {
            /**
             * 환불상품 필터링하고 보여주기
             */
            const returns =
              value?.goodsIssueItems?.[0]?.deliveryList
                ?.filter((item: Delivery) => item.deliveryType === "RETURN")
                .map((item: Delivery) => item.deliveryNo) || [];

            // 회송된 상품 제외 하고 보여주기
            return value?.goodsIssueItems?.[0]?.deliveryList
              ?.filter(
                (item: Delivery) =>
                  item.deliveryType === "DELIVERY" &&
                  !returns.includes(item.deliveryNo)
              )
              .map((item: Delivery) => item.trackingNo || "-")
              .join(", ");
          },
        },
        {
          dataIndex: "repairStatus",
          key: "repairStatus",
          title: (
            <Tooltip
              placement="bottomRight"
              color={color.gray12}
              title={<ReservationNoticeInfo />}
              showArrow={false}
              overlayStyle={{
                minWidth: 420,
              }}
              overlayInnerStyle={{
                padding: 16,
              }}
            >
              <Space size={4} align="center">
                <Typography.Text color={color.gray4}>예약상태</Typography.Text>
                <IconForm
                  component={InterfaceNotice}
                  color={color.gray5}
                  style={{ fontSize: 15 }}
                />
              </Space>
            </Tooltip>
          ),
          width: 120,
          align: "center",
          render: (value, record) => {
            const status = record.onSite?.onSiteStatus || value;
            return (
              <div style={{ display: "inline-flex" }}>
                <RepairStatusTag value={status} size="medium" />
              </div>
            );
          },
        },
        Table.SELECTION_COLUMN,
        {
          dataIndex: "button",
          key: "button",
          title: "",
          width: 98,
          align: "center",
          fixed: "right",
          render: (_, record) => {
            return record?.repairStatus ===
              PartnerBosRepairStatuses.RESERVATION_COMPLETED ||
              record.onSite?.onSiteStatus === "APPROVED" ? (
              <Button
                ghost
                type="primary"
                disabled={moment().isBefore(moment(record.reservedAt))}
                onClick={(e) => {
                  e.stopPropagation();
                  openConfirmModal({
                    contents: "해당 항목을 '작업완료'하시겠습니까?",
                    title: "작업완료",
                    onOk: () => {
                      completeServiceMutateAsync(record.repairNo);
                    },
                    okText: "작업완료",
                  });
                }}
              >
                작업완료
              </Button>
            ) : (
              "-"
            );
          },
        },
      ] as TableColumnType<Reservation>[],
    [master]
  );

  /**
   * 일괄 작업완료 요청
   */
  const handleAllCompleted = () => {
    if (selectedRowKeys.length) {
      openConfirmModal({
        title: "일괄 작업완료 요청",
        contents: `선택 된 ${selectedRowKeys.length}개 주문에 대하여 일괄 작업완료를 진행하시겠습니까?`,
        onOk: () => {
          bulkCompleteServiceMutate(selectedRowKeys as string[]);
        },
      });
    } else {
      openAlertModal({
        title: "선택된 주문 없음",
        contents: "선택된 주문이 없습니다.",
      });
    }
  };

  /**
   * 테이블 동작 이벤트 핸들러
   * @param pagination
   */
  const handleChange = (pagination: PaginationProps) => {
    setFilterItems((prev) => ({ ...prev, page: pagination.current }));
  };

  useEffect(() => {
    if (shop) {
      setFilterItems((prev) => ({
        ...prev,
        page: 1,
        pageSize: prev.pageSize || 10,
        repairShopId: +shop,
      }));
    }
  }, [shop]);

  const scrollY = useMemo(
    () =>
      (containerRef.current?.clientHeight || window.innerHeight - 57 - 57) -
      (filterRef.current?.clientHeight || 260) -
      24 -
      48 -
      112,
    [width, height, containerRef, filterRef, expended]
  );

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", height: "100%", flexDirection: "column" }}
    >
      <Space
        direction="vertical"
        size={0}
        style={{ background: color.gray12, padding: 0 }}
      >
        <section ref={filterRef}>
          <ReservationFilterForm
            onSearch={(value) => {
              setFilterItems((prev) => ({
                ...prev,
                ...value,
                page: 1,
                times: Date.now(),
              }));
            }}
            onExpended={setExpended}
          />
        </section>
        <Divider style={{ margin: 0 }} />
        <CustomCard
          title={<Typography.Title level={4}>예약 리스트</Typography.Title>}
          extra={
            <Row gutter={[16, 0]} align="middle">
              <Col>
                <TablePagiCountForm
                  onSelect={(value: number) => {
                    setFilterItems((prev) => ({
                      ...prev,
                      page: 1,
                      pageSize: value,
                    }));
                  }}
                  defaultValue={filterItems.pageSize}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  disabled={selectedRowKeys.length < 1}
                  onClick={handleAllCompleted}
                >
                  일괄 작업완료
                </Button>
              </Col>
            </Row>
          }
          bordered={false}
          headStyle={{ borderBottomWidth: 0 }}
          bodyStyle={{ padding: "8px 24px 20px" }}
        >
          <TableForm
            rowKey="repairNo"
            columns={columns.filter((c) => Object.keys(c).length !== 1)}
            dataSource={data?.rows || []}
            loading={isRefetching || isLoading}
            rowClassName={(record) =>
              record.repairNo === reservation.repairNo ? "highlight" : ""
            }
            onRow={(record) => ({
              style: {
                cursor: record ? "pointer" : "default",
              },
              onClick: (e) => {
                e.stopPropagation();
                if (record && reservation.repairNo !== record.repairNo) {
                  // repairNo 외 정보 제거되는것 방지용 동일 주문 클릭 제어
                  setReservation({ repairNo: record.repairNo });
                }
              },
            })}
            rowSelection={{
              getCheckboxProps: (record) => ({
                disabled:
                  record?.repairStatus !==
                    PartnerBosRepairStatuses.RESERVATION_COMPLETED ||
                  moment().isBefore(moment(record.reservedAt)),
                name: record.reservedAt,
              }),
              onChange: (selecedRowKeys) => {
                setSelectedRowKeys([...selecedRowKeys]);
              },
            }}
            onChange={handleChange}
            pagination={{
              current: filterItems.page,
              pageSize: filterItems.pageSize,
              total: data?.count,
              showSizeChanger: false,
              size: "default",
            }}
            scroll={{
              x: 1000,
              y: scrollY,
            }}
            footer={() => <TableFooterCount total={data?.count} />}
          />
        </CustomCard>
      </Space>
    </div>
  );
};
